import { Stack, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking/booking_context'
import { useLocationContext } from '@providers/location'
import { isUserLoggedIn } from '@providers/session/helpers'
import { useSessionContext } from '@providers/session/session_context'
import { buildPetGroups } from '@shared/booking_groups'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import { isWaitlistEnabled } from '@shared/locations'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import { useRedirectAllOffersUnavailable } from 'hooks/use_redirect_all_offers_unavailable'
import { BoardingIcon } from 'components-ui/src/asset/boarding_icon'
import { DaycareIcon } from 'components-ui/src/asset/daycare_icon'
import { OfferCard } from 'components-ui/src/offer_card/offer_card'
import type { TOfferSelectionOptions } from 'components-ui/src/offer_card/offer_card.d'
import { Spacer, Spacer3 } from 'components-ui/src/spacer/spacer'
import { useClientBookingSearchByDays } from 'service-api/src/hooks/use_client_booking_search'
import { AvailabilityGroupClientDto, OfferClientDto } from 'service-api/src/types/api'
import { configureTemperTest, listPetNames, setUpTemperTest, sortOffers } from './helper'
import { SearchOffersMembershipAlert } from './search_offers_membership_alert'
import { SearchResultsSkeleton } from './search_result_skeleton'

export const SearchOffersByDay = () => {
  const { accountName = '', serviceTypeName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { location } = useLocationContext()
  const { navigateRoute } = useRoutes()
  const { state: s } = bookingCtx
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const [isSchedulingTemperTest, setIsSchedulingTemperTest] = useState(false)

  // request initial availability without dates to avoid incorrect results when daycare calls for the full month later
  const searchResult = useClientBookingSearchByDays(accountName, serviceTypeName, {
    petGroups: buildPetGroups(bookingCtx.state, false, false),
    offersType: 'PRIMARY',
    enabled: !isSchedulingTemperTest,
  })

  const waitlistEnabled = (location?.subscriberGroup?.waitlistEnabled ?? false) && isWaitlistEnabled(bookingCtx.state)

  // redirect to the "Contact Us To Book" page when every offer is unavailable
  useRedirectAllOffersUnavailable({
    offers: searchResult.data?.results,
    isSuccess: searchResult.isSuccess,
    isEnabled: !isSchedulingTemperTest,
    waitlistEnabled,
  })

  useEffect(() => {
    if (bookingCtx.state.currentState !== 'searchResults') {
      bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'searchResults' } })
    }
  }, [bookingCtx])

  useEffect(() => {
    if (searchResult.isSuccess) {
      bookingCtx.dispatch({ type: 'LOAD_OFFERS_SEARCH_RESULTS', payload: { value: searchResult.data?.results || [] } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult.isSuccess, searchResult.data?.results])

  // redirect to home if the state has invalid data
  useEffect(() => {
    if (!s.startDate) {
      navigateRoute('searchServiceType', { serviceTypeName })
    }
  })

  const onSelectOffer = useCallback(
    async (
      offer: OfferClientDto,
      availability: AvailabilityGroupClientDto,
      petGroup: number,
      options: TOfferSelectionOptions
    ) => {
      bookingCtx.dispatch({ type: 'SELECT_OFFER', payload: { offer, availability, petGroup } })
      if (!options.holdNavigation) {
        if (!isUserLoggedIn(sessionCtx.state.user)) {
          navigateRoute('loginCheck', { accountName, serviceTypeName })
        } else {
          navigateRoute('datesSelection', { serviceTypeName })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookingCtx, navigateRoute, serviceTypeName]
  )

  const onSelectScheduleTempTest = useCallback(
    async (affectedByPetIds: string[]) => {
      if (!isUserLoggedIn(sessionCtx.state.user)) {
        navigateRoute('loginCheck', { accountName, serviceTypeName })
      } else {
        setIsSchedulingTemperTest(true)
        try {
          setUpTemperTest(bookingCtx.state, bookingCtx.dispatch, affectedByPetIds)
          await configureTemperTest({
            temperTestId: bookingCtx.state.data.temperTest?.id || '',
            dispatch: bookingCtx.dispatch,
            accountName,
          })
          setTimeout(() => navigateRoute('temperTest', { serviceTypeName: 'temper-test' }), 0)
        } catch (e) {
          captureError({ cause: e } as Error)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookingCtx, navigateRoute, serviceTypeName]
  )

  const totalPets = useMemo(
    () => bookingCtx.state.cart.groups.reduce((acc, g) => acc + g.pets.length, 0),
    [bookingCtx.state.cart.groups]
  )

  const sortedOffers = useMemo(() => {
    if (searchResult.isSuccess) {
      return sortOffers(searchResult.data?.results, serviceTypeName)
    } else {
      return []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult.data?.results, searchResult.isSuccess])

  const renderResult = () =>
    sortedOffers.map((result) => (
      <Fragment key={result.offer!.id}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
          <OfferCard
            key={result.offer!.id}
            searchResult={result}
            petGroup={0} // for daycare there is always 1 pet group
            onSelectOffer={onSelectOffer}
            totalPets={totalPets}
            serviceTypeName={serviceTypeName}
            specieTypeName={s.cart.groups[0].specieName}
            selected={bookingCtx.state.cart.groups[0]?.offerSelected?.offer?.id === result.offer?.id}
            hasMembershipsService={!!bookingCtx.state.data.membershipService}
            temperTest={bookingCtx.state.data.temperTest}
            onSelectScheduleTempTest={onSelectScheduleTempTest}
            petNames={listPetNames(bookingCtx.state, 0)}
          />
        </Paper>
        <Spacer3 />
      </Fragment>
    ))

  const pageTitle =
    bookingCtx.state.serviceTypeName === 'boarding' ? 'searchResults.title.boarding' : 'searchResults.title.daycare'

  const iconTitle =
    bookingCtx.state.serviceTypeName === 'boarding' ? (
      <BoardingIcon color="primary" sx={{ width: 40, height: 40 }} />
    ) : (
      <DaycareIcon color="primary" fill={theme.palette.primary.main} sx={{ width: 40, height: 40 }} />
    )

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Stack direction="row" alignItems="center">
        {iconTitle}
        <Typography variant="h2">{t(pageTitle)}</Typography>
      </Stack>
      <Spacer3 />
      <SearchOffersMembershipAlert
        hasMembershipsService={!!bookingCtx.state.data.membershipService}
        searchResults={searchResult?.data?.results}
        isOvernight
      />
      <Spacer3 />
      {searchResult.isLoading ? <SearchResultsSkeleton /> : renderResult()}
    </Box>
  )
}
